import { io } from 'socket.io-client';
import { socketUrl } from '../../env';
import events from 'events';
import { parseInfoConsoleMessageDataToText } from './ConsoleMessageHelper';
import { getPlayerConsoleIconPath } from '../../env';
import { analytics } from './AnalyticsManager';

// Copy of the 'GAME_STATE' values from the gameModel.js in the server-side code.
export const GAME_STATE =
{
  PLACING_TILE: 0,
  PLACING_MEEPLE: 1,
  END_GAME_PHASE: 2,
  GAME_OVER: 3

  // Future states...
  // MOVING_DRAGON: 3,
  // MOVING_FAIRY: 4
};

export default class GameStateManager
{
  eventEmitter = new events.EventEmitter();

  socket = undefined;
  socketClientID = undefined;
  socketConnectedRoomId = undefined;
  socketClientIndex = undefined;

  socketRoomOptions = undefined;
  socketRoomHasStarted = undefined;

  numGameStateChangeListeners = 0;
  timerID = null;

  onRoomChatMessageCallbacks = [];
  onEmojiReactionCallbacks = [];
  onGameInfoConsoleMessageCallbacks = [];
  onGameInfoConsoleTileHighlightRequestCallbacks = [];
  onAutoCameraFramingModeChangeCallbacks = [];
  onMeepleReturnAnimationRequestCallbacks = [];
  onStageRenderDataChangeCallbacks = [];
  onConnectionStatusChangeCallbacks = [];

  autoCameraFramingModeState = true;

  gameStateDataDirty = false;

  // Reconnection state
  isReconnecting = false;
  reconnectAttempts = 0;
  maxReconnectAttempts = 10;
  reconnectInterval = 5000; // 5 seconds
  reconnectTimer = null;

  canvasRefWidth = 1350;
  canvasRefHeight = 833;

  // Pixi stage render data

  stageRenderData = {

    width: this.canvasRefWidth,
    height: this.canvasRefHeight,
    scale: 1.0,

    stageStyleTop: 0,
    showLeftRightAds: false,
    showBottomAd: false
  };

  // Room Game State Data

  gameStateData = {

    hasStarted: false,
    gameState: -1,
    boardTiles: null,
    boardMeeples: null,
    boardFields: null,
    boardSquareTileHashBL: null,
    boardSquareTileHashTR: null,
    playerData: null,
    currentTileSprite: null,
    numTilesRemaining: null,
    tileIdCounts: null,
    timerData: null,
    isPlayerTurn: false,
    validTilePlacements: null,
    validMeeplePlacements: null,
    lastTilesPlacedByPlayers: null,
    doesBoardHaveFarmers: false
  };

  createBotGame( onRoomConnect, onConnectionError )
  {
    this.resetStateData();

    // Open a new socket io connection on the server and a new Room

    this.socket = io( `${socketUrl}`,
      {
        transports: [ 'websocket' ],	// disable HTTP long-polling
        query: {
          action: 'createVsBot',
          // tokenData: JSON.stringify( tokenData )
        }
      } );

    this.initListeners( onRoomConnect, onConnectionError );

    analytics.track( 'createBotRoom' );
  }


  createNewRoomSocket( onRoomConnect, onConnectionError )
  {
    this.resetStateData();

    // Open a new socket io connection on the server and a new Room

    this.socket = io( `${socketUrl}`,
      {
        transports: [ 'websocket' ],	// disable HTTP long-polling
        query: {
          action: 'create',
          // tokenData: JSON.stringify( tokenData )
        }
      } );

    this.initListeners( onRoomConnect, onConnectionError );

    analytics.track( 'createNewRoom' );
  }


  joinExistingRoomSocket( roomId, onRoomConnect, onConnectionError, resetStateData = true )
  {
    if( resetStateData )
    {
      this.resetStateData();
    }

    // Attempt to join an existing room and open a socket io connection

    this.socket = io( `${socketUrl}`,
      {
        transports: [ 'websocket' ],	// disable HTTP long-polling
        query: {
          action: 'join',
          // tokenData: JSON.stringify( tokenData ),
          roomId: roomId
        }
      } );

    this.initListeners( onRoomConnect, onConnectionError );
  }


  createBugReportPlaybackSocket( bugReportId,
    onRoomConnect,
    onConnectionError,
    skipToTileIndex = 0,
    skipToFeatureResolveIndex = 0,
    resolveAILogic = true,
    playbackTurnSpeed = 2 )
  {
    this.resetStateData();

    // Open a new socket io connection on the server and a new Room for bug report playback

    this.socket = io( `${socketUrl}`,
      {
        transports: [ 'websocket' ],	// disable HTTP long-polling
        query: {
          action: 'debug',
          bugReportId: bugReportId,
          skipToTileIndex: skipToTileIndex,
          skipToFeatureResolveIndex: skipToFeatureResolveIndex,
          playbackTurnSpeed: playbackTurnSpeed,
          resolveAILogic: resolveAILogic
        }
      } );

    this.socket.isDebugPlayback = true;

    this.initListeners( onRoomConnect, onConnectionError );
  }

  createPlayedGamePlaybackSocket( playedGameId,
    onRoomConnect,
    onConnectionError,
    skipToTileIndex = 0,
    skipToFeatureResolveIndex = 0,
    resolveAILogic = true,
    playbackTurnSpeed = 2 )
  {
    this.resetStateData();

    // Open a new socket io connection on the server and a new Room for bug report playback

    this.socket = io( `${socketUrl}`,
      {
        transports: [ 'websocket' ],	// disable HTTP long-polling
        query: {
          action: 'debug',
          playedGameId: playedGameId,
          skipToTileIndex: skipToTileIndex,
          skipToFeatureResolveIndex: skipToFeatureResolveIndex,
          playbackTurnSpeed: playbackTurnSpeed,
          resolveAILogic: resolveAILogic
        }
      } );

    this.socket.isDebugPlayback = true;

    this.initListeners( onRoomConnect, onConnectionError );
  }


  /* 														*/
  /* Data / Event Callback Register/Unregister functions 	*/
  /* 														*/

  onRoomErrorRegister( callback )
  {
    this.eventEmitter.on( 'onRoomError', callback );
  }

  onRoomErrorUnregister( callback )
  {
    this.eventEmitter.removeListener( 'onRoomError', callback );
  }

  onGameStartRegister( callback )
  {
    this.eventEmitter.on( 'onGameStarted', callback );
  }

  onGameStartUnregister( callback )
  {
    this.eventEmitter.removeListener( 'onGameStarted', callback );
  }

  onGameStateChangedRegister( callback )
  {
    this.numGameStateChangeListeners++;

    this.eventEmitter.on( 'onGameStateChanged', callback );

    // Send the current game state data
    callback( this.gameStateData );

    if( !this.timerID )
    {
      this.timerID = setInterval(
        () => this._tick(),
        100
      );
    }
  }

  onGameStateChangedUnregister( callback )
  {
    this.numGameStateChangeListeners--;

    this.eventEmitter.removeListener( 'onGameStateChanged', callback );

    if( this.numGameStateChangeListeners <= 0 )
    {
      // No more active listeners, so stop the tick interval
      clearInterval( this.timerID );

      this.timerID = null;
    }
  }

  _tick()
  {
    // console.log("state change tick... Dirty", this.gameStateDataDirty );

    if( this.gameStateDataDirty )
    {
      // console.log( "GAME STATE DATA DIRTY - EMITTING LATEST" );

      this.eventEmitter.emit( 'onGameStateChanged', this.gameStateData );

      this.gameStateDataDirty = false;
    }
  }

  getSocketClientIndex( forceFetch = false )
  {
    if( ( this.socketClientIndex === undefined || forceFetch ) && this.gameStateData.playerData )
    {
      for( let pIdx = 0; pIdx < this.gameStateData.playerData.playersJoined.length; pIdx++ )
      {
        let playerData = this.gameStateData.playerData.playersJoined[ pIdx ];

        if( playerData.socketID === this.socketClientID )
        {
          this.socketClientIndex = pIdx;

          break;
        }
      }
    }

    return this.socketClientIndex;
  }

  isInEndGamePhaseState()
  {
    return this.gameStateData.gameState === GAME_STATE.END_GAME_PHASE;
  }

  getUserCurrentGameRanking()
  {
    let clientIndex = this.getSocketClientIndex();

    if( clientIndex === undefined )
    {
      return -1;
    }

    let playerScores = this.gameStateData.playerData.scores;

    if( !playerScores || playerScores.length == 0 || clientIndex < 0 || clientIndex >= playerScores.length )
    {
      return -2;
    }

    let clientScore = playerScores[ clientIndex ];

    // Sort scores descending, remove duplicates
    let uniqueScores = Array.from( new Set( playerScores ) ).sort( ( a, b ) => b - a );

    let ranking = uniqueScores.indexOf( clientScore ) + 1;
    return ranking;
  }

  getUserGameAnalyticsData()
  {
    return {
      currentGameState: this.gameStateData.gameState,
      currentGameRanking: this.getUserCurrentGameRanking(),
      numTilesRemaining: this.gameStateData.numTilesRemaining,
      percentageThroughTheGame: 1.0 - ( this.gameStateData.numTilesRemaining / 72.0 )
    };
  }



  resetStateData()
  {
    if( this.socket )
    {
      this.socket.close();
    }

    this.socketClientID = undefined;
    this.socketConnectedRoomId = undefined;
    this.socketRoomOptions = undefined;
    this.socketRoomHasStarted = undefined;

    // NOTE: Don't reset reconnection parameters here - they should persist through reconnection attempts
    // Only clear them when explicitly stopping reconnection or when successful
  }

  addOnRoomChatMessageCallback( callback )
  {
    this.onRoomChatMessageCallbacks.push( callback );
  }

  removeOnRoomChatMessageCallback( callback )
  {
    let index = this.onRoomChatMessageCallbacks.indexOf( callback );

    if( index < 0 )
    {
      // Didn't find it. Nothing to remove.
      return;
    }

    this.onRoomChatMessageCallbacks.splice( index, 1 );
  }

  broadcastRoomChatMessage( message )
  {
    for( let idx = 0; idx < this.onRoomChatMessageCallbacks.length; idx++ )
    {
      this.onRoomChatMessageCallbacks[ idx ]( message );
    }
  }

  addOnEmojiReactionCallback( callback )
  {
    this.onEmojiReactionCallbacks.push( callback );
  }

  removeOnEmojiReactionCallback( callback )
  {
    let index = this.onEmojiReactionCallbacks.indexOf( callback );

    if( index < 0 )
    {
      // Didn't find it. Nothing to remove.
      return;
    }

    this.onEmojiReactionCallbacks.splice( index, 1 );
  }

  broadcastEmojiReaction( reactionData )
  {
    for( let idx = 0; idx < this.onEmojiReactionCallbacks.length; idx++ )
    {
      this.onEmojiReactionCallbacks[ idx ]( reactionData );
    }
  }

  addOnGameInfoConsoleMessageCallback( callback )
  {
    this.onGameInfoConsoleMessageCallbacks.push( callback );
  }

  removeOnGameInfoConsoleMessageCallback( callback )
  {
    let index = this.onGameInfoConsoleMessageCallbacks.indexOf( callback );

    if( index < 0 )
    {
      // Didn't find it. Nothing to remove.
      return;
    }

    this.onGameInfoConsoleMessageCallbacks.splice( index, 1 );
  }

  broadcastGameInfoConsoleMessage( message )
  {
    for( let idx = 0; idx < this.onGameInfoConsoleMessageCallbacks.length; idx++ )
    {
      this.onGameInfoConsoleMessageCallbacks[ idx ]( message );
    }
  }

  addOnGameInfoConsoleTileHighlightRequestCallback( callback )
  {
    this.onGameInfoConsoleTileHighlightRequestCallbacks.push( callback );
  }

  removeOnGameInfoConsoleTileHighlightRequestCallback( callback )
  {
    let index = this.onGameInfoConsoleTileHighlightRequestCallbacks.indexOf( callback );

    if( index < 0 )
    {
      // Didn't find it. Nothing to remove.
      return;
    }

    this.onGameInfoConsoleTileHighlightRequestCallbacks.splice( index, 1 );
  }

  broadcastGameInfoConsoleTileHighlightRequest( tileCoords )
  {
    for( let idx = 0; idx < this.onGameInfoConsoleTileHighlightRequestCallbacks.length; idx++ )
    {
      this.onGameInfoConsoleTileHighlightRequestCallbacks[ idx ]( tileCoords );
    }
  }


  addOnAutoCameraFramingModeChangeCallback( callback )
  {
    this.onAutoCameraFramingModeChangeCallbacks.push( callback );
  }

  removeOnAutoCameraFramingModeChangeCallback( callback )
  {
    let index = this.onAutoCameraFramingModeChangeCallbacks.indexOf( callback );

    if( index < 0 )
    {
      // Didn't find it. Nothing to remove.
      return;
    }

    this.onAutoCameraFramingModeChangeCallbacks.splice( index, 1 );
  }

  setAutoCameraFramingModeState( state )
  {
    this.autoCameraFramingModeState = state;

    for( let idx = 0; idx < this.onAutoCameraFramingModeChangeCallbacks.length; idx++ )
    {
      this.onAutoCameraFramingModeChangeCallbacks[ idx ]( state );
    }
  }


  addOnMeepleReturnAnimationRequestCallback( callback )
  {
    this.onMeepleReturnAnimationRequestCallbacks.push( callback );
  }

  removeOnMeepleReturnAnimationRequestCallback( callback )
  {
    let index = this.onMeepleReturnAnimationRequestCallbacks.indexOf( callback );

    if( index < 0 )
    {
      // Didn't find it. Nothing to remove.
      return;
    }

    this.onMeepleReturnAnimationRequestCallbacks.splice( index, 1 );
  }

  broadcastMeepleReturnAnimationRequest( data )
  {
    for( let idx = 0; idx < this.onMeepleReturnAnimationRequestCallbacks.length; idx++ )
    {
      this.onMeepleReturnAnimationRequestCallbacks[ idx ]( data );
    }
  }




  addOnStageRenderDataChangeCallback( callback )
  {
    this.onStageRenderDataChangeCallbacks.push( callback );

    return this.stageRenderData;
  }

  removeOnStageRenderDataChangeCallback( callback )
  {
    let index = this.onStageRenderDataChangeCallbacks.indexOf( callback );

    if( index < 0 )
    {
      // Didn't find it. Nothing to remove.
      return;
    }

    this.onStageRenderDataChangeCallbacks.splice( index, 1 );
  }

  setNewStageRenderDataState( { width, height, scale, stageStyleTop, showLeftRightAds, showBottomAd } )
  {
    if( width !== undefined )
    {
      this.stageRenderData.width = width;
    }
    if( height !== undefined )
    {
      this.stageRenderData.height = height;
    }
    if( scale !== undefined )
    {
      this.stageRenderData.scale = scale;
    }
    if( stageStyleTop !== undefined )
    {
      this.stageRenderData.stageStyleTop = stageStyleTop;
    }
    if( showLeftRightAds !== undefined )
    {
      this.stageRenderData.showLeftRightAds = showLeftRightAds;
    }
    if( showBottomAd !== undefined )
    {
      this.stageRenderData.showBottomAd = showBottomAd;
    }

    for( let idx = 0; idx < this.onStageRenderDataChangeCallbacks.length; idx++ )
    {
      this.onStageRenderDataChangeCallbacks[ idx ]( this.stageRenderData );
    }
  }

  addOnConnectionStatusChangeCallback( callback )
  {
    this.onConnectionStatusChangeCallbacks.push( callback );

    // Send current connection status
    callback( {
      isReconnecting: this.isReconnecting,
      reconnectAttempts: this.reconnectAttempts
    } );
  }

  removeOnConnectionStatusChangeCallback( callback )
  {
    let index = this.onConnectionStatusChangeCallbacks.indexOf( callback );

    if( index < 0 )
    {
      // Didn't find it. Nothing to remove.
      return;
    }

    this.onConnectionStatusChangeCallbacks.splice( index, 1 );
  }

  broadcastConnectionStatusChange( status )
  {
    for( let idx = 0; idx < this.onConnectionStatusChangeCallbacks.length; idx++ )
    {
      this.onConnectionStatusChangeCallbacks[ idx ]( status );
    }
  }

  startReconnectionProcess( onRoomConnect, onConnectionError )
  {
    if( !this.socketConnectedRoomId )
    {
      console.log( 'Reconnection not started: No RoomID found.' );
      return;
    }

    if( this.isReconnecting )
    {
      console.log( 'Reconnection not started:', {
        isReconnecting: this.isReconnecting
      } );
      return;
    }

    console.log( 'Starting reconnection process' );

    this.isReconnecting = true;
    this.reconnectAttempts = 0;

    this.broadcastConnectionStatusChange( {
      isReconnecting: true,
      reconnectAttempts: this.reconnectAttempts
    } );

    this.attemptReconnection( onRoomConnect, onConnectionError );
  }

  attemptReconnection( onRoomConnect, onConnectionError )
  {
    if( !this.isReconnecting )
    {
      this.stopReconnectionProcess();
      return;
    }

    this.reconnectAttempts++;

    this.broadcastConnectionStatusChange( {
      isReconnecting: true,
      reconnectAttempts: this.reconnectAttempts
    } );

    // Clear any existing timer
    if( this.reconnectTimer )
    {
      clearTimeout( this.reconnectTimer );
    }

    if( this.reconnectAttempts > this.maxReconnectAttempts )
    {
      // Can't retry any more...
      return;
    }

    console.log( `Attempting reconnection to room ${this.socketConnectedRoomId} - ${this.reconnectAttempts}/${this.maxReconnectAttempts}` );

    // Attempt to reconnect based on the last connection type
    try
    {
      this.joinExistingRoomSocket( this.socketConnectedRoomId, onRoomConnect, onConnectionError, false /* resetStateData */ );
    }
    catch( error )
    {
      console.error( 'Error during reconnection attempt:', error );
      this.scheduleNextReconnectionAttempt( onRoomConnect, onConnectionError );
    }
  }

  scheduleNextReconnectionAttempt( onRoomConnect, onConnectionError )
  {
    if( !this.isReconnecting )
    {
      return;
    }

    this.reconnectTimer = setTimeout( () =>
    {
      this.attemptReconnection( onRoomConnect, onConnectionError );
    }, this.reconnectInterval );
  }

  stopReconnectionProcess()
  {
    if( !this.isReconnecting )
    {
      return;
    }

    console.log( 'Stopping reconnection process' );

    this.isReconnecting = false;
    this.reconnectAttempts = 0;

    if( this.reconnectTimer )
    {
      clearTimeout( this.reconnectTimer );
      this.reconnectTimer = null;
    }

    this.broadcastConnectionStatusChange( {
      isReconnecting: false,
      reconnectAttempts: 0
    } );
  }


  initListeners( onRoomConnect, onConnectionError )
  {
    this.initSocketConnectionStatusListeners( this.socket, onRoomConnect, onConnectionError );

    this.initGameStateChangeListeners( this.socket );

    this.initGameErrorListeners( this.socket );

    window.addEventListener( 'beforeunload', this.beforeUnloadHandler );
  }

  beforeUnloadHandler = () =>
  {
    if( this.socket )
    {
      this.stopReconnectionProcess();

      this.socket.disconnect();

      if( !this.socket.isDebugPlayback )
      {
        analytics.track( 'game.Exited', this.getUserGameAnalyticsData() );
      }
    }
  };

  /**
   * Shows an error message and then redirects to lobby after a delay
   * This ensures the error toast has time to display before navigation
   */
  async showErrorAndRedirectToLobby( onConnectionError, errorData, redirectDelay = 3000 )
  {
    // Show the error message first
    onConnectionError( errorData );

    // Wait a brief moment for the error message to be displayed
    await new Promise( resolve => setTimeout( resolve, redirectDelay ) );

    // Then redirect to the lobby
    window.location.href = "/";
  }


  initSocketConnectionStatusListeners( socket, onRoomConnect, onConnectionError )
  {
    socket.on( 'connect', () =>
    {
      // console.log( "Socket Connected :: client socketID : " + socket.id );
    } );

    // socket.on( 'disconnect', () =>
    // {
    // if( !this.socket.isDebugPlayback )
    //   {
    //   analytics.track( 'game.Exited', this.getUserGameAnalyticsData() );
    // }
    // } );


    socket.on( 'success-room-entered', ( { roomId, options, gameStarted } ) =>
    {
      // console.log( 'Room Connection Successfully initialised. gameStarted : ' + gameStarted );

      this.socketConnectedRoomId = roomId;
      this.socketClientID = socket.id;
      this.socketRoomOptions = options;
      this.socketRoomHasStarted = gameStarted;

      // Connection successful, stop any reconnection process
      this.stopReconnectionProcess();

      if( gameStarted )
      {
        this.eventEmitter.emit( 'onGameStarted' );

        if( !this.socket.isDebugPlayback )
        {
          analytics.track( 'game.Rejoined', this.getUserGameAnalyticsData() );
        }
      }

      onRoomConnect( roomId );
    } );

    socket.on( 'connect_error', ( data ) =>
    {
      // data here should be an Error object sent by server
      console.log( "On connection_error", data );

      socket.close();

      this.socketClientID = undefined;

      // Start reconnection process if we have connection parameters and are not already reconnecting
      if( !this.isReconnecting )
      {
        this.startReconnectionProcess( onRoomConnect, onConnectionError );
      }
      else
      {
        // We're already reconnecting, so schedule the next attempt
        this.scheduleNextReconnectionAttempt( onRoomConnect, onConnectionError );
      }
    } );


    socket.on( 'error-room-already-full', () =>
    {
      console.log( 'Error: Room is already full.' );

      socket.close();

      // Stop reconnection for permanent errors
      this.stopReconnectionProcess();

      onConnectionError( {
        title: 'ROOM FULL',
        content: 'Sorry, this room is already full.'
      } );
    } );

    socket.on( 'error-previously-kicked-from-room', () =>
    {
      console.log( 'Error: Player previously kicked from room.' );

      socket.close();

      // Stop reconnection for permanent errors
      this.stopReconnectionProcess();

      onConnectionError( {
        title: 'PREVIOUSLY KICKED',
        content: 'You were kicked from this room.'
      } );
    } );

    socket.on( 'error-user-already-in-room', () =>
    {
      console.log( 'Error: Player is already in this room.' );

      socket.close();

      // Stop reconnection for permanent errors
      this.stopReconnectionProcess();

      onConnectionError( {
        title: 'USER ALREADY IN ROOM',
        content: 'It appears that you are already playing in this room.'
      } );
    } );

    socket.on( 'error-user-reached-max-room-limit', () =>
    {
      console.log( 'Error: User has reached ther max room limit.' );

      socket.close();

      onConnectionError( {
        title: 'MAX GAME LIMIT REACHED',
        content: 'You already have the maximum number of active games in progress. You need to wait for one of those to finish before starting or joining another.'
      } );
    } );

    socket.on( 'error-room-already-started', () =>
    {
      console.log( 'error-room-already-started' );

      socket.close();

      onConnectionError( {
        title: 'GAME ALREADY STARTED',
        content: 'Sorry, this room has already started playing.'
      } );
    } );

    socket.on( 'error-room-not-found', () =>
    {
      console.log( 'Error : Create a room first!' );

      socket.close();

      // Stop reconnection for permanent errors
      this.stopReconnectionProcess();

      onConnectionError( {
        title: 'ROOM NOT FOUND',
        content: 'Sorry, requested Room does not exist. Create a New Room or enter the correct ROOM ID'
      } );
    } );

    socket.on( 'error-room-no-longer-exists', () =>
    {
      console.log( 'Error: Socket returned error - Room no longer exists.' );

      socket.close();

      onConnectionError( {
        title: 'Room No Longer Exists',
        content: "The room you were in doesn't seem to exist anymore. Please create or join another one."
      } );

      const urlObject = new URL( window.location.href );
      const rootDomainUrl = urlObject.origin;

      // Delay redirect for a few seconds to allow time to view the toast error message
      setTimeout( () =>
      {
        window.location.replace( rootDomainUrl );
      }, 3000 );
    } );

    socket.on( 'Error: Room already created. Join the room!', () =>
    {
      console.log( 'Error: Create a new room again or Join existing one!' );

      socket.close();

      onConnectionError( {
        title: 'ROOM ALREADY PRESENT',
        content: 'Sorry, requested Room already exists, Join the existing room or Create a new room again'
      } );
    } );

    socket.on( 'error-set-team-colour-failed', () =>
    {
      console.log( 'Error: Team colour not available.' );

      onConnectionError( {
        title: 'COLOUR UNAVAILABLE',
        content: 'Team colour not available. Please choose another.'
      } );
    } );

    socket.on( 'host-kicked-player-from-room', () =>
    {
      console.log( 'Host kicked player from the room.' );

      socket.close();

      // Show error message then redirect to lobby
      this.showErrorAndRedirectToLobby( onConnectionError, {
        title: 'PLAYER KICKED',
        content: 'The host kicked you from the room.'
      } );
    } );

    socket.on( 'player-joined-room-from-other-client', () =>
    {
      socket.close();

      // Show error message then redirect to lobby
      this.showErrorAndRedirectToLobby( onConnectionError, {
        title: 'MULTIPLE ROOM LOG INS',
        content: 'User joined via another browser. This instance will been disconnected.'
      } );
    } );
  };


  initGameStateChangeListeners( socket )
  {
    socket.on( 'room-settings-changed', ( { options } ) =>
    {
      this.socketRoomOptions = options;

      this.gameStateDataDirty = true;

    } );

    socket.on( 'game-start', message =>
    {
      this.socketRoomHasStarted = true;

      this.eventEmitter.emit( 'onGameStarted' );

      this.broadcastGameInfoConsoleMessage( { line: true } );
      this.broadcastGameInfoConsoleMessage( { text: "Good luck. Have fun!" } );
      this.broadcastGameInfoConsoleMessage( { line: true } );



      if( !this.socket.isDebugPlayback )
      {
        // console.log( 'game.start', this.socketRoomOptions, this.gameStateData );

        let totalNumPlayers = this.gameStateData.playerData.playersJoined.length;
        let totalNumHumans = 0;
        let totalNumBots = 0;
        let isClientGameHost = false;

        for( let pIdx = 0; pIdx < this.gameStateData.playerData.playersJoined.length; pIdx++ )
        {
          let playerData = this.gameStateData.playerData.playersJoined[ pIdx ];

          totalNumHumans += playerData.isBot ? 0 : 1;
          totalNumBots += playerData.isBot ? 1 : 0;

          if( playerData.socketID === this.socketClientID )
          {
            isClientGameHost = playerData.isHost;
          }
        }

        analytics.track( 'game.Start',
          {
            ...this.socketRoomOptions,
            roomId: this.socketConnectedRoomId,
            totalNumPlayers: totalNumPlayers,
            numHumans: totalNumHumans,
            numBots: totalNumBots,
            isHost: isClientGameHost
          } );
      }

    } );

    socket.on( 'chat-text-received', ( chatTextData ) =>
    {
      // {username: 'TomLocal', chatText: 'FARTS'}
      // {chatBotMessageID: 'inappropriate-language'}

      if( chatTextData.chatBotMessageID )
      {
        // A message from the chat bot!
        let chatBotMessage = "";

        switch( chatTextData.chatBotMessageID )
        {
          case 'inappropriate-language':
            chatBotMessage = "Please avoid inappropriate language.";
            break;

          case 'spectator-cant-chat':
            chatBotMessage = "Spectators can't use the room chat.";
            break;

          case 'chat-temp-blocked':
            chatBotMessage = "Chat usage temporarily blocked.";
            break;

          default:
            chatBotMessage = chatTextData.chatBotMessageID;
            break;
        }

        this.broadcastRoomChatMessage( { chatBotMessage: chatBotMessage } );
      }
      else
      {
        this.broadcastRoomChatMessage( chatTextData );
      }
    } );

    socket.on( 'emoji-reaction-received', ( reactionData ) =>
    {
      // {playerName: 'PlayerName', emoji: '👍', playerIconID: 'default', teamColourID: 'red'}
      if( reactionData && reactionData.playerName && reactionData.emoji )
      {
        let messageIconPath;
        if( typeof reactionData.playerIconID !== 'undefined' )
        {
          messageIconPath = getPlayerConsoleIconPath( reactionData.playerIconID, reactionData.teamColourID );
        }

        this.broadcastEmojiReaction( {
          playerName: reactionData.playerName,
          emoji: reactionData.emoji,
          messageIconPath: messageIconPath
        } );
      }
    } );

    socket.on( 'game-console-message', ( messageData ) =>
    {
      this.broadcastGameInfoConsoleMessage( parseInfoConsoleMessageDataToText( messageData ) );
    } );

    socket.on( 'board-anim-meeple-returned', ( data ) =>
    {
      this.broadcastMeepleReturnAnimationRequest( data );
    } );

    socket.on( 'game-state-data-changed', ( data ) =>
    {
      if( !data )
      {
        return;
      }

      if( data.hasStarted !== undefined )
      {
        this.gameStateData.hasStarted = data.hasStarted;
      }

      if( data.gameState !== undefined )
      {
        if( this.gameStateData.gameState < 0 )
        {
          // First gameState data value in this play session
          this.gameStateData.gameState = data.gameState;
        }
        else if( this.gameStateData.gameState !== data.gameState )
        {
          // Game state has changed
          this.gameStateData.gameState = data.gameState;

          if( data.gameState === GAME_STATE.END_GAME_PHASE )
          {
            // Entered into End Game Phase

            if( !this.socket.isDebugPlayback )
            {
              analytics.track( 'game.AllTilesPlaced', { currentGameRanking: this.getUserCurrentGameRanking() } );
            }
          }
          else if( data.gameState === GAME_STATE.GAME_OVER )
          {
            // Game is Complete - shown results panel

            if( !this.socket.isDebugPlayback )
            {
              analytics.track( 'game.Complete', { currentGameRanking: this.getUserCurrentGameRanking() } );
            }

            window.removeEventListener( 'beforeunload', this.beforeUnloadHandler );
          }
        }
      }

      if( this.gameStateData.gameState === GAME_STATE.PLACING_TILE )
      {
        if( data.tileSprite !== undefined )
        {
          this.gameStateData.currentTileSprite = data.tileSprite;
        }
      }
      else
      {
        this.gameStateData.currentTileSprite = null;
      }

      if( data.numTilesRemaining !== undefined )
      {
        this.gameStateData.numTilesRemaining = data.numTilesRemaining;
      }

      if( data.tileIdCounts !== undefined )
      {
        this.gameStateData.tileIdCounts = data.tileIdCounts;
      }

      if( data.timerData !== undefined )
      {
        this.gameStateData.timerData = data.timerData;
      }

      if( data.playerData !== undefined )
      {
        this.gameStateData.playerData = data.playerData;
      }

      if( data.boardData !== undefined )
      {
        this.gameStateData.boardMeeples = data.boardData.meeples;
        this.gameStateData.boardTiles = data.boardData.tiles;
        this.gameStateData.boardFields = data.boardData.fields;
        this.gameStateData.boardSquareTileHashBL = data.boardData.boardSquareTileHashBL;
        this.gameStateData.boardSquareTileHashTR = data.boardData.boardSquareTileHashTR;
      }

      if( data.validTilePlacements !== undefined )
      {
        this.gameStateData.validTilePlacements = data.validTilePlacements;
      }

      if( data.opponentValidTilePlacements !== undefined )
      {
        this.gameStateData.opponentValidTilePlacements = data.opponentValidTilePlacements;
      }

      if( data.validMeeplePlacements !== undefined )
      {
        this.gameStateData.validMeeplePlacements = data.validMeeplePlacements;
      }

      if( data.lastTilesPlacedByPlayers !== undefined )
      {
        this.gameStateData.lastTilesPlacedByPlayers = data.lastTilesPlacedByPlayers;
      }

      if( data.doesBoardHaveFarmers !== undefined )
      {
        this.gameStateData.doesBoardHaveFarmers = data.doesBoardHaveFarmers;
      }

      this.gameStateDataDirty = true;

      console.log( "EMIT game state change" );
    } );
  }



  initGameErrorListeners( socket )
  {
    socket.on( 'Error: Room resize unavailable. Remove room clients first.', () =>
    {
      console.log( 'Error: Room resize unavailable. Remove room clients first.' );

      this.eventEmitter.emit( 'onRoomError', {
        title: 'ROOM RESIZE UNAVAILABLE',
        content: 'The room already has more players than the requested room size change. Remove players before attempting to reduce the Max Room Size.'
      } );

    } );
  }


}






